/**
 * Attachment queries and mutations.
 *
 * No detail query. An attachment has no page of its own — it is always shown
 * in the context of the record it hangs off — so there is nothing for a detail
 * cache to serve, and adding one would be a cache nothing reads.
 *
 * Every mutation invalidates the lists rather than seeding a cache: uploading
 * changes what the list contains, deleting removes a row, and a download
 * changes the count PRD §5.10 makes visible. None of those can be patched in
 * from a single response.
 *
 * In `lib/` rather than a feature, following `activities.queries` — and for
 * the same reason. An attachment hangs off a startup OR a pilot, so both
 * features need these hooks, and plan §3.1 lets a feature import only its own
 * api/ layer. A shared data layer therefore has to sit where every layer may
 * reach it.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.8, PRD §5.10
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  AttachmentSummary,
  DownloadLink,
  ListAttachmentsQuery,
  UpdateAttachmentRequest,
} from '@nexus/contracts';

import * as attachmentsApi from './attachments.api';
import type { AttachmentPage, UploadFields } from './attachments.api';
import { queryKeys } from './query-keys';

export function useAttachments(
  query: ListAttachmentsQuery,
): UseQueryResult<AttachmentPage> {
  return useQuery({
    queryKey: queryKeys.attachments.list(query),
    queryFn: () => attachmentsApi.listAttachments(query),
  });
}

/** Invalidates every attachment list. */
function useInvalidateAttachments(): () => void {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.attachments.lists() });
  };
}

export function useUploadAttachment(): UseMutationResult<
  AttachmentSummary,
  Error,
  { file: File; fields: UploadFields }
> {
  const invalidate = useInvalidateAttachments();

  return useMutation({
    mutationFn: ({ file, fields }: { file: File; fields: UploadFields }) =>
      attachmentsApi.uploadAttachment(file, fields),
    onSuccess: invalidate,
  });
}

export function useUpdateAttachment(): UseMutationResult<
  AttachmentSummary,
  Error,
  { id: string; body: UpdateAttachmentRequest }
> {
  const invalidate = useInvalidateAttachments();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAttachmentRequest }) =>
      attachmentsApi.updateAttachment(id, body),
    onSuccess: invalidate,
  });
}

export function useDeleteAttachment(): UseMutationResult<
  void,
  Error,
  { id: string; version: number }
> {
  const invalidate = useInvalidateAttachments();

  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      attachmentsApi.deleteAttachment(id, version),
    onSuccess: invalidate,
  });
}

/**
 * Asks for a download link.
 *
 * A mutation rather than a query, because it has an effect: the server logs
 * the download (PRD §5.10). A `useQuery` would also cache the URL, which is
 * wrong twice over — the link expires, and a cached one would stop the log
 * recording the second read.
 *
 * The lists are invalidated so the download count updates.
 */
export function useDownloadLink(): UseMutationResult<DownloadLink, Error, string> {
  const invalidate = useInvalidateAttachments();

  return useMutation({
    mutationFn: (id: string) => attachmentsApi.createDownloadLink(id),
    onSuccess: invalidate,
  });
}
